import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import ThemeToggle from "@/components/ThemeToggle";
import ScrambleText from "@/components/ScrambleText";
import lionLogoDark from "@/assets/lion-logo-dark.png";
import lionLogoLight from "@/assets/lion-logo-light.png";

type NavLink =
  | { label: string; href: string }
  | { label: string; to: string }
  | {
      label: string;
      dropdown: Array<{
        label: string;
        to: string;
        description?: string;
      }>;
    };

const navLinks: NavLink[] = [
  {
    label: "Product",
    dropdown: [
      { label: "Overview", to: "/product", description: "Everything Mane is and does" },
      { label: "Agents", to: "/agents", description: "The full roster, by tier" },
      { label: "AI Agent MCPs", to: "/mcp", description: "Business MCPs for your agent" },
      { label: "Architecture", to: "/architecture", description: "How the system fits together" },
    ],
  },
  { label: "Pricing", to: "/pricing" },
  {
    label: "Compare",
    dropdown: [
      { label: "vs ChatGPT (Codex)", to: "/compare/chatgpt", description: "One generalist vs. a roster of specialists" },
      { label: "vs Claude (in Slack)", to: "/compare/claude", description: "From one bot to a whole team" },
      { label: "vs OpenClaw", to: "/compare/openclaw", description: "Managed Business MCPs, no setup" },
      { label: "vs Hermes", to: "/compare/hermes", description: "Ready to ship, on your Claude" },
      { label: "vs Viktor", to: "/compare/viktor", description: "Same work, a fraction of the cost" },
    ],
  },
  {
    label: "Resources",
    dropdown: [
      { label: "Example Library", to: "/examples", description: "Real outputs you can download" },
      { label: "Integrations", to: "/integrations", description: "3,000+ tools, SOC 2 Type 2" },
      { label: "Learn", to: "/learn", description: "AI agents, end to end" },
      { label: "Docs", to: "/docs", description: "Get started and reference" },
      { label: "Blog", to: "/blog", description: "Field notes from the team" },
      { label: "Changelog", to: "/changelog", description: "What's new in Mane" },
    ],
  },
  { label: "Get Mane", to: "/get-mane" },
];

const Navbar = ({ variant = "default" }: { variant?: "default" | "overlay" }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [hovered, setHovered] = useState<string | null>(null);
  const [logoHover, setLogoHover] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const dropdownTimer = useRef<number | null>(null);
  const location = useLocation();
  const isLandingOverlay = variant === "overlay" && location.pathname === "/";
  const scrolledEffective = scrolled || mobileOpen;
  const isOverlay = isLandingOverlay && !scrolledEffective;

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    handler();
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const enterDropdown = (label: string) => {
    if (dropdownTimer.current) window.clearTimeout(dropdownTimer.current);
    setOpenDropdown(label);
  };

  const leaveDropdown = () => {
    if (dropdownTimer.current) window.clearTimeout(dropdownTimer.current);
    dropdownTimer.current = window.setTimeout(() => setOpenDropdown(null), 120);
  };

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className={`${isLandingOverlay ? "fixed" : "sticky"} top-0 left-0 right-0 z-[60] transition-[padding,border-color,background-color] duration-[600ms] ease-[cubic-bezier(0.22,1,0.36,1)] border-b ${
          scrolledEffective
            ? "pt-2 sm:pt-4 border-transparent"
            : isOverlay
            ? "pt-2 sm:pt-4 border-transparent"
            : "border-foreground/15"
        }`}
      >
        <div
          className={`mx-auto transition-[max-width,border-radius,background-color,box-shadow,backdrop-filter] duration-[600ms] ease-[cubic-bezier(0.22,1,0.36,1)] will-change-[max-width,box-shadow] ${
            scrolledEffective
              ? "max-w-[min(1180px,calc(100%-0.5rem))] sm:max-w-[min(1180px,calc(100%-1.5rem))] rounded-full border border-border bg-background/75 backdrop-blur-md supports-[backdrop-filter]:bg-background/60 shadow-[0_10px_30px_-12px_rgba(15,12,8,0.18),0_2px_8px_-4px_rgba(15,12,8,0.08)] dark:shadow-[0_14px_40px_-16px_rgba(0,0,0,0.65),0_2px_8px_-4px_rgba(0,0,0,0.4)]"
              : isLandingOverlay
              ? "max-w-[min(1180px,calc(100%-0.5rem))] sm:max-w-content"
              : "max-w-content"
          }`}
        >
          <div className="hidden lg:flex items-center justify-between h-14 px-6">
            {/* Left: wordmark */}
            <Link
              to="/"
              onMouseEnter={() => setLogoHover(true)}
              onMouseLeave={() => setLogoHover(false)}
              onFocus={() => setLogoHover(true)}
              onBlur={() => setLogoHover(false)}
              className="flex items-center gap-2.5 group -mx-2 px-2 py-2 relative z-10"
            >
              <img decoding="async" src={isOverlay ? lionLogoLight : lionLogoDark} alt="" aria-hidden="true" width={24} height={24} fetchPriority="high" data-no-fade className="w-6 h-6 object-contain block dark:hidden" />
              <img decoding="async" src={lionLogoLight} alt="" aria-hidden="true" width={24} height={24} fetchPriority="high" data-no-fade className="w-6 h-6 object-contain hidden dark:block" />
              <span className="inline-block w-[68px] overflow-hidden">
                <ScrambleText
                  text="MANE"
                  trigger={logoHover}
                  duration={420}
                  className={`font-serif font-normal text-[22px] tracking-[-0.01em] leading-none inline-block whitespace-nowrap ${isOverlay ? "text-media-foreground" : "text-foreground"}`}
                />
              </span>
            </Link>

            {/* Middle: nav links, flat plain text, underline-on-hover only */}
            <div className="flex items-center gap-4">
              {navLinks.map((link) => {
                const inner = (
                  <span
                    className={`inline-flex items-center gap-1 font-sans text-[14px] font-normal leading-none hover:underline hover:decoration-foreground underline-offset-[4px] decoration-[1px] transition-colors ${
                      isOverlay ? "text-media-foreground/90 hover:decoration-media-foreground" : "text-foreground"
                    }`}
                  >
                    {link.label}
                    {"dropdown" in link && (
                      <ChevronDown
                        className={`w-3 h-3 transition-transform ${
                          openDropdown === link.label ? "rotate-180" : ""
                        } ${isOverlay ? "text-media-foreground/70" : ""}`}
                      />
                    )}
                  </span>
                );

                if ("dropdown" in link) {
                  return (
                    <div
                      key={link.label}
                      className="relative"
                      onMouseEnter={() => {
                        setHovered(link.label);
                        enterDropdown(link.label);
                      }}
                      onMouseLeave={() => {
                        setHovered(null);
                        leaveDropdown();
                      }}
                    >
                      <button
                        type="button"
                        onClick={() =>
                          setOpenDropdown((c) => (c === link.label ? null : link.label))
                        }
                        className="px-[10px] py-0 leading-none"
                        aria-haspopup="menu"
                        aria-expanded={openDropdown === link.label}
                      >
                        {inner}
                      </button>

                      <AnimatePresence>
                        {openDropdown === link.label && (
                          <motion.div
                            initial={{ opacity: 0, y: -4 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -4 }}
                            transition={{ duration: 0.15 }}
                            className="absolute top-full mt-2 z-50 left-0 w-[280px]"
                            role="menu"
                          >
                            <div className="rounded-[5px] border border-border bg-background overflow-hidden shadow-[0_24px_48px_-12px_rgba(15,12,8,0.28),0_8px_16px_-8px_rgba(15,12,8,0.18),0_0_0_1px_rgba(15,12,8,0.04)] dark:shadow-[0_24px_48px_-12px_rgba(0,0,0,0.7),0_8px_16px_-8px_rgba(0,0,0,0.55),0_0_0_1px_rgba(255,255,255,0.04)]">
                              {link.dropdown.map((item) => (
                                <Link
                                  key={item.to}
                                  to={item.to}
                                  onClick={() => setOpenDropdown(null)}
                                  className="block px-4 py-3 hover:bg-secondary/60 transition-colors border-b border-border/60 last:border-b-0"
                                  role="menuitem"
                                >
                                  <p className="font-sans text-[14px] text-foreground mb-0.5">
                                    {item.label}
                                  </p>
                                  {item.description && (
                                    <p className="text-[12px] text-muted-foreground leading-snug">
                                      {item.description}
                                    </p>
                                  )}
                                </Link>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                }

                if ("to" in link && link.label === "Get Mane") {
                  return null;
                }

                return "to" in link ? (
                  <Link key={link.label} to={link.to} className="px-[10px] py-0 leading-none">
                    {inner}
                  </Link>
                ) : (
                  <a key={link.label} href={link.href} className="px-[10px] py-0 leading-none">
                    {inner}
                  </a>
                );
              })}
            </div>

            {/* Right: theme toggle, Sign up, Dashboard */}
            <div className="flex items-center gap-3">
              <ThemeToggle className={isOverlay ? "text-media-foreground/85 hover:text-media-foreground" : undefined} />

              {/* Dashboard, text link */}
              <a
                href="https://auth.mane.dev"
                className={`inline-flex items-center gap-1 font-sans text-[14px] font-normal leading-none hover:underline hover:decoration-foreground underline-offset-[4px] decoration-[1px] ${
                  isOverlay ? "text-media-foreground/90 hover:decoration-media-foreground" : "text-foreground hover:decoration-foreground"
                }`}
              >
                Dashboard
              </a>

              <Link
                to="/get-mane"
                className={`inline-flex items-center px-4 h-9 rounded-full font-sans text-[14px] font-medium leading-none shadow-[0_1px_2px_rgba(0,0,0,0.18),0_8px_24px_-12px_rgba(0,0,0,0.35)] hover:-translate-y-px transition-all ${
                  isOverlay
                    ? "bg-white text-[#0a0a0a] hover:bg-white/95"
                    : "bg-foreground text-background hover:opacity-90"
                }`}
              >
                Get Mane
              </Link>
            </div>
          </div>

          {/* Mobile bar */}
          <div className="lg:hidden flex items-center justify-between h-11 sm:h-12 px-3.5 sm:px-5">
            <Link to="/" className="flex items-center gap-2">
              <img decoding="async" src={isOverlay ? lionLogoLight : lionLogoDark} alt="" aria-hidden="true" width={24} height={24} fetchPriority="high" data-no-fade className="w-6 h-6 sm:w-7 sm:h-7 object-contain block dark:hidden" />
              <img decoding="async" src={lionLogoLight} alt="" aria-hidden="true" width={24} height={24} fetchPriority="high" data-no-fade className="w-6 h-6 sm:w-7 sm:h-7 object-contain hidden dark:block" />
              <span className={`font-serif text-[19px] sm:text-[22px] tracking-[-0.01em] leading-none ${isOverlay ? "text-media-foreground" : "text-foreground"}`}>
                MANE
              </span>
            </Link>
            <div className="flex items-center gap-2">
              <ThemeToggle className={isOverlay ? "text-media-foreground/85 hover:text-media-foreground" : undefined} />
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className={`p-2 -mr-2 min-w-[44px] min-h-[44px] flex items-center justify-center ${isOverlay ? "text-media-foreground" : "text-foreground"}`}
                aria-label="Toggle menu"
              >
                {mobileOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>
        </div>
      </motion.nav>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-40 pt-12"
          >
            <div className="absolute inset-0 bg-background" />
            <div className="relative h-full overflow-y-auto p-5">
              <div className="flex flex-col gap-1">
                {navLinks.map((link) => {
                  if ("dropdown" in link) {
                    const expanded = mobileExpanded === link.label;
                    return (
                      <div
                        key={link.label}
                        className="border-b border-border/60"
                      >
                        <button
                          type="button"
                          onClick={() =>
                            setMobileExpanded((c) => (c === link.label ? null : link.label))
                          }
                          className="w-full px-4 py-3 font-mono text-[13px] uppercase tracking-[0.2em] text-foreground hover:bg-secondary rounded-none transition-colors min-h-[44px] flex items-center justify-between"
                        >
                          <span>{link.label}</span>
                          <ChevronDown
                            className={`w-4 h-4 transition-transform ${
                              expanded ? "rotate-180" : ""
                            }`}
                          />
                        </button>
                        {expanded && (
                          <div className="pb-3 pl-4 flex flex-col gap-1">
                            {link.dropdown.map((item) => (
                              <Link
                                key={item.to}
                                to={item.to}
                                onClick={() => setMobileOpen(false)}
                                className="px-4 py-2.5 font-mono text-[12px] uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground hover:bg-secondary rounded-none transition-colors min-h-[40px] flex items-center"
                              >
                                {item.label}
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  }
                  return "to" in link ? (
                    <Link
                      key={link.label}
                      to={link.to}
                      onClick={() => setMobileOpen(false)}
                      className="px-4 py-3 font-mono text-[13px] uppercase tracking-[0.2em] text-foreground hover:bg-secondary rounded-none transition-colors min-h-[44px] flex items-center border-b border-border/60"
                    >
                      {link.label}
                    </Link>
                  ) : (
                    <a
                      key={link.label}
                      href={link.href}
                      onClick={() => setMobileOpen(false)}
                      className="px-4 py-3 font-mono text-[13px] uppercase tracking-[0.2em] text-foreground hover:bg-secondary rounded-none transition-colors min-h-[44px] flex items-center border-b border-border/60"
                    >
                      {link.label}
                    </a>
                  );
                })}
                <Link
                  to="/get-mane"
                  onClick={() => setMobileOpen(false)}
                  className="mt-4 inline-flex items-center justify-center gap-1.5 rounded-full h-12 font-sans text-[14px] font-medium bg-foreground text-background shadow-[0_1px_2px_rgba(0,0,0,0.18)]"
                >
                  Get Mane <span aria-hidden>→</span>
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
